import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { OUTREACH_LEVELS, type OutreachAllowed, OutreachLevel, type RequestOutreach } from "../../../utils/models";
import { type LevelLadderLevel, LevelLadderState } from "../../UIToolkit/DataViz/SystemLevelLadder/SystemLevelLadder";
import { levelIndexOf, levelNumberOf } from "./outreachGroupTypes";
import { dayMetaOf, type OutreachCopy } from "./useOutreachCopy";

/** Where Today sits inside the current level when there's no next start to measure against, and how near its edges it may go. */
const UNTIMED_PROGRESS = 0.5;
const MIN_PROGRESS = 0.1;
const MAX_PROGRESS = 0.9;

/** The levels that only add something when it's allowed for this shift. */
const NEEDS: Partial<Record<OutreachLevel, keyof OutreachAllowed>> = {
	[OutreachLevel.Calls]: "calls",
	[OutreachLevel.Overtime]: "overtime",
	[OutreachLevel.Agency]: "agency",
};

export const isLevelAllowed = (allowed: OutreachAllowed, level: OutreachLevel): boolean => {
	const need = NEEDS[level];
	return need === undefined || allowed[need];
};

export type EffortLadderView = { title: string; levels: LevelLadderLevel[]; progress: number; markerLabel: string | null };

/** The next level that will start on its own; none while holding. */
const nextIndexOf = (outreach: RequestOutreach): number => {
	const { effort } = outreach;
	if (effort.holding) return -1;
	return effort.schedule.findIndex((entry, index) => index > levelIndexOf(effort.level) && entry.startsAt !== null);
};

/** How far along the whole ladder Today is: through the current level, by how much of its time to the next has passed. */
export const progressOf = (outreach: RequestOutreach, now: DateTime): number => {
	const { schedule, level } = outreach.effort;
	const current = levelIndexOf(level);
	const from = schedule[current].startsAt;
	if (from === null) throw new Error(`Outreach: the current level ${level} has no start`);
	const next = schedule[current + 1];
	// The last level, or one held below the next, has no next start to measure against.
	const to = next === undefined ? null : next.startsAt;
	const within = to === null ? UNTIMED_PROGRESS : Math.min(MAX_PROGRESS, Math.max(MIN_PROGRESS, now.diff(from).toMillis() / to.diff(from).toMillis()));
	return (current + within) / OUTREACH_LEVELS.length;
};

const captionOf = (t: TFunction, outreach: RequestOutreach, index: number, state: LevelLadderState, now: DateTime, isNext: boolean): string | undefined => {
	const { effort } = outreach;
	switch (state) {
		case LevelLadderState.Done:
			// A level done above our pick was jumped over by the user, not worked through.
			return t(index > levelIndexOf(effort.automaticLevel) ? "request.outreach.effort.caption.skipped" : "request.outreach.effort.caption.done");
		case LevelLadderState.Current:
			return t(effort.setByUser ? "request.outreach.effort.caption.now_by_you" : "request.outreach.effort.caption.now");
		case LevelLadderState.Held:
			return t("request.outreach.effort.caption.holding");
		case LevelLadderState.Upcoming: {
			const startsAt = effort.schedule[index].startsAt;
			return isNext && startsAt !== null ? dayMetaOf(t, startsAt, now) : undefined;
		}
	}
};

/** The Fill effort ladder: each level's bar and caption, Today along the track (hidden while paused), and the title over it. */
export const effortLadderOf = (t: TFunction, copy: OutreachCopy, outreach: RequestOutreach, now: DateTime, paused: boolean): EffortLadderView => {
	const { effort } = outreach;
	const current = levelIndexOf(effort.level);
	const next = paused ? -1 : nextIndexOf(outreach);
	const levels = effort.schedule.map(({ level, startsAt }, index): LevelLadderLevel => {
		const state =
			index < current
				? LevelLadderState.Done
				: index === current
					? LevelLadderState.Current
					: startsAt === null
						? LevelLadderState.Held
						: LevelLadderState.Upcoming;
		return {
			id: level,
			tag: t(`request.outreach.level.${level}.tag`),
			label: copy.levelName(level),
			caption: captionOf(t, outreach, index, state, now, index === next),
			captionIsNext: index === next,
			struck: !isLevelAllowed(effort.allowed, level),
			state,
			dataTestId: `outreach-level-${level}`,
		};
	});
	const title = t(effort.setByUser ? "request.outreach.effort.level_title_by_you" : "request.outreach.effort.level_title", {
		number: levelNumberOf(effort.level),
		total: OUTREACH_LEVELS.length,
	});
	return { title, levels, progress: progressOf(outreach, now), markerLabel: paused ? null : t("request.outreach.effort.today") };
};
